import type { InternalUiSpec } from "./catalog";

/**
 * A demonstration internal-UI spec that exercises the whole catalog. Used by the
 * frontend demo page and the catalog validation test. It shows the three kinds
 * of dynamism json-render supports: static props, two-way binding
 * (`$bindState` on the Input), and state-bound data (`$state` on the Table).
 */
export const internalUiSampleSpec: InternalUiSpec = {
  root: "page",
  elements: {
    page: {
      type: "Stack",
      props: { gap: "lg", padding: "lg" },
      children: ["hero", "statusRow", "form", "orders", "note"],
    },
    hero: {
      type: "Card",
      props: {
        title: "Internal UI",
        description:
          "This screen is described by JSON and rendered with @erp/ui.",
      },
      children: ["heroText"],
    },
    heroText: {
      type: "Text",
      props: {
        value:
          "Edit the spec and the preview updates live — no server round-trip.",
        variant: "muted",
      },
      children: [],
    },
    statusRow: {
      type: "Box",
      props: { direction: "row", gap: "sm", align: "center" },
      children: [
        "statusEyebrow",
        "badgeOperational",
        "badgePending",
        "badgeDraft",
      ],
    },
    statusEyebrow: {
      type: "Text",
      props: { value: "Status", variant: "eyebrow" },
      children: [],
    },
    badgeOperational: {
      type: "Badge",
      props: { label: "Operational", variant: "success" },
      children: [],
    },
    badgePending: {
      type: "Badge",
      props: { label: "3 pending", variant: "warning" },
      children: [],
    },
    badgeDraft: {
      type: "Badge",
      props: { label: "Draft", variant: "secondary" },
      children: [],
    },
    form: {
      type: "Card",
      props: { title: "Quick action" },
      children: ["formStack"],
    },
    formStack: {
      type: "Stack",
      props: { gap: "sm" },
      children: ["nameLabel", "nameInput", "formDivider", "submit"],
    },
    nameLabel: {
      type: "Text",
      props: { value: "Supplier name", variant: "label" },
      children: [],
    },
    nameInput: {
      type: "Input",
      props: {
        placeholder: "Acme Foods",
        value: { $bindState: "/form/supplierName" },
      },
      children: [],
    },
    formDivider: {
      type: "Separator",
      props: {},
      children: [],
    },
    submit: {
      type: "Button",
      props: { label: "Create order", variant: "default" },
      children: [],
    },
    orders: {
      type: "Card",
      props: { title: "Recent orders" },
      children: ["ordersTable"],
    },
    ordersTable: {
      type: "Table",
      props: {
        columns: [
          { key: "po", label: "PO" },
          { key: "vendor", label: "Vendor" },
          { key: "total", label: "Total", align: "right" },
        ],
        rows: { $state: "/orders" },
      },
      children: [],
    },
    note: {
      type: "Alert",
      props: {
        title: "Heads up",
        description:
          "Color in this system only ever signals status, never decoration.",
        variant: "default",
      },
      children: [],
    },
  },
};

/** Initial state for {@link internalUiSampleSpec}. */
export const internalUiSampleState: Record<string, unknown> = {
  form: { supplierName: "" },
  orders: [
    { po: "PO-1042", vendor: "Acme Foods", total: "$4,200.00" },
    { po: "PO-1043", vendor: "Northwind Trading", total: "$1,890.00" },
    { po: "PO-1044", vendor: "Birchwood Mills", total: "$12,540.00" },
  ],
};
